import { useForm, useWatch } from "react-hook-form";
import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import OperatorSelect from "@via/components/advanced-filter/operator-select";
import ValueInput from "@via/components/advanced-filter/value-input";
import {
  FieldType,
  FieldTypeEnum,
} from "@via/components/advanced-filter/helpers/types";
import { getFieldTypeFromString } from "@via/components/advanced-filter/helpers/utils";
import { SelectOption } from "@via/utils/interfaces";
import { type JSX, useEffect } from "react";
import { MetadataFilter } from "@via/pages/sample-trackers/samples/types/filters.ts";

interface AddMetadataFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddFilter: (filter: MetadataFilter) => void;
  fields: FieldType[];
  editingFilter?: MetadataFilter;
}

interface FilterFormData {
  field: string;
  operator: string;
  value: string | number | boolean | Date;
}

export default function AddMetadataFilterModal({
  isOpen,
  onClose,
  onAddFilter,
  fields,
  editingFilter,
}: AddMetadataFilterModalProps): JSX.Element {
  const isEditMode = !!editingFilter;

  const { control, handleSubmit, reset, watch } = useForm<FilterFormData>({
    defaultValues: {
      field: editingFilter?.field || "",
      operator: editingFilter?.operator || "contains",
      value: editingFilter?.value || "",
    },
  });

  // Reset form when editingFilter changes or modal opens/closes
  useEffect(() => {
    if (isOpen) {
      reset({
        field: editingFilter?.field || "",
        operator: editingFilter?.operator || "contains",
        value: editingFilter?.value || "",
      });
    }
  }, [isOpen, editingFilter, reset]);

  const selectedField = useWatch({
    control,
    name: "field",
  });

  // Find the selected field to get its type
  const fieldType = selectedField
    ? getFieldTypeFromString(
        fields.find(f => f.name === selectedField)?.type || "String"
      )
    : FieldTypeEnum.String;

  // Convert fields to SelectOption format
  const fieldOptions: SelectOption[] = fields.map(field => ({
    label: field.label,
    value: field.name,
  }));

  const onSubmit = (data: FilterFormData): void => {
    if (
      data.field &&
      data.operator &&
      data.value !== undefined &&
      data.value !== null &&
      (typeof data.value === "boolean" || data.value !== "")
    ) {
      onAddFilter({
        field: data.field,
        operator: data.operator,
        value: data.value,
      });
      reset();
      onClose();
    }
  };

  const isFormValid = (): boolean => {
    const formValues = watch();
    return !!(
      formValues.field &&
      formValues.operator &&
      formValues.value !== undefined &&
      formValues.value !== null &&
      (typeof formValues.value === "boolean" || formValues.value !== "")
    );
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title={isEditMode ? "Edit Metadata Filter" : "Add Metadata Filter"}
      showButtons={false}
      size="small">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Field Selection */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-900">
            Select field
          </label>
          <SelectComponent
            control={control}
            name="field"
            options={fieldOptions}
            placeholder="Select field"
            enableClear={false}
            readOnly={isEditMode}
          />
        </div>

        {/* Operator Selection */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-900">
            Operator
          </label>
          <OperatorSelect
            control={control}
            name="operator"
            fieldType={fieldType}
            placeholder="contains"
            enableClear={false}
            disabled={!selectedField}
          />
        </div>

        {/* Value Input */}
        <div>
          <ValueInput
            control={control}
            name="value"
            fieldType={fieldType}
            placeholder="Enter value"
            disabled={!selectedField}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
            {isEditMode ? "Update Filter" : "Add Filter"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
